import * as THREE from "three";
import { PMREMGenerator } from "three";
import { Sky } from "./sky";
import { DaylightRig, type DaylightRigConfig } from "./daylight";
import { AssetPipeline } from "../core/assets";
import type { MissionConfig } from "../mission/types";

export type SceneEnvironmentConfig = {
  hdriPath?: string;
  showSky?: boolean;
  backgroundColor?: number;
  fogColor?: number;
  fogDensity?: number;
  daylight?: DaylightRigConfig;
};

// Per-map environment presets. HDRIs are CC0, Poly Haven — see
// public/ASSET_MANIFEST_VISUAL.md. Fog color must sit close to each HDRI's
// horizon tone so distance haze and the ridge rings dissolve into the sky;
// the daylight rig follows the HDRI's mood (map02 flies at golden hour).
export const MAP_ENVIRONMENT: Record<MissionConfig["mapId"], SceneEnvironmentConfig> = {
  current: {
    hdriPath: "/textures/sky/kloofendal_48d_partly_cloudy_puresky_2k.hdr",
  },
  // GREEN HAMMER is pre-dawn mist over triple canopy: a cool, dim, damp
  // world. The mist does the atmosphere work — fog is denser than any other
  // map and the key is pale and low.
  "green-hammer": {
    hdriPath: "/textures/sky/kloofendal_misty_morning_puresky_2k.hdr",
    fogColor: 0xaebcac,
    fogDensity: 0.0004,
    daylight: {
      keyColor: 0xe8f0e2,
      keyIntensity: 1.3,
      keyPosition: new THREE.Vector3(420, 150, -300), // low pale sun through mist
      ambientColor: 0x9cab9a,
      ambientIntensity: 0.7,
      fillColor: 0x7e8ba0,
      fillIntensity: 0.42,
    },
  },
  // ARCTIC WOLF is perpetual grey twilight: bright cold ambient (snow
  // bounce), pale blue-grey key, thin cold fog. Snow albedo already sits
  // below pure white so the key has headroom.
  "arctic-wolf": {
    hdriPath: "/textures/sky/kloppenheim_06_puresky_2k.hdr",
    fogColor: 0xb9c2cc,
    fogDensity: 0.00038,
    daylight: {
      keyColor: 0xd8e2ec,
      keyIntensity: 1.2,
      keyPosition: new THREE.Vector3(-460, 130, 340), // sun that never rises far
      ambientColor: 0xb4bec8,
      ambientIntensity: 0.85,
      fillColor: 0x8a98b0,
      fillIntensity: 0.45,
    },
  },
  // IRON CURTAIN is autumn overcast: soft shadowless grey light, cold fog.
  // The warm field patchwork carries the color; the light stays neutral.
  "iron-curtain": {
    hdriPath: "/textures/sky/overcast_soil_puresky_2k.hdr",
    fogColor: 0xa8aca6,
    fogDensity: 0.00036,
    daylight: {
      keyColor: 0xdde2e4,
      keyIntensity: 1.25,
      keyPosition: new THREE.Vector3(-300, 240, -420), // high diffuse "sun"
      ambientColor: 0xa2a8a4,
      ambientIntensity: 0.78,
      fillColor: 0x8790a0,
      fillIntensity: 0.4,
    },
  },
  // IRON MARKET flies the same golden hour as the valley but chokes it with
  // city dust — browner fog, denser, softer key.
  "iron-market": {
    hdriPath: "/textures/sky/qwantani_sunset_puresky_2k.hdr",
    fogColor: 0xc4ad88,
    fogDensity: 0.0004,
    daylight: {
      keyColor: 0xffd0a0,
      keyIntensity: 1.45,
      keyPosition: new THREE.Vector3(-480, 200, -180),
      ambientColor: 0xb5a58c,
      ambientIntensity: 0.66,
      fillColor: 0x8a8ea8,
      fillIntensity: 0.38,
    },
  },
  "river-valley": {
    hdriPath: "/textures/sky/qwantani_sunset_puresky_2k.hdr",
    fogColor: 0xc2b3a0,
    // Slightly denser than the old uniform fog — the height-aware haze
    // (vfx/haze.ts) thins it on ridgelines, so the floor can afford more soup.
    fogDensity: 0.0004,
    daylight: {
      // Warm but not orange — a deeper orange key was cooking the dark
      // high-altitude browns into desert-dune orange on sunlit faces.
      keyColor: 0xffd6a6,
      keyIntensity: 1.6, // punchy directional light — flat-shaded facets live on it
      keyPosition: new THREE.Vector3(-520, 180, 260), // low warm sun
      ambientColor: 0xb0a08c,
      ambientIntensity: 0.62,
      fillColor: 0x7d88a8,
      fillIntensity: 0.4,
    },
  },
};

const DEFAULT_CONFIG: Required<Omit<SceneEnvironmentConfig, "hdriPath" | "daylight">> = {
  showSky: true,
  backgroundColor: 0x91a07f,
  // Neutral pale blue-grey — must sit close to the HDRI's horizon tone so
  // fogged ground and the ridge rings dissolve into the sky instead of
  // banding green against it.
  fogColor: 0xaeb6b0,
  fogDensity: 0.00038,
};

export class SceneEnvironment {
  readonly daylight: DaylightRig;
  readonly sky: Sky | null;
  private pmrem: PMREMGenerator | null = null;
  private envRenderTarget: THREE.WebGLRenderTarget | null = null;
  private backgroundTexture: THREE.Texture | null = null;
  private readonly mergedConfig: Required<Omit<SceneEnvironmentConfig, "hdriPath" | "daylight">> & Pick<SceneEnvironmentConfig, "hdriPath" | "daylight">;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    private readonly assets: AssetPipeline,
    config: SceneEnvironmentConfig = {},
  ) {
    const merged = { ...DEFAULT_CONFIG, ...config };
    this.mergedConfig = merged;
    this.scene.background = new THREE.Color(merged.backgroundColor);
    this.scene.fog = new THREE.FogExp2(merged.fogColor, merged.fogDensity);

    this.daylight = new DaylightRig(this.scene, config.daylight ?? {});
    this.sky = merged.showSky
      ? new Sky({
        zenithColor: 0x365d8d,
        horizonColor: 0xb3be92,
        nadirColor: 0x5a6652,
        sunColor: 0xffefbf,
        sunDirection: this.daylight.key.position.clone().normalize(),
        horizonSoftness: 0.66,
        hazeStrength: 0.24,
        sunGlow: 0.28,
        sunSharpness: 24,
      })
      : null;
    if (this.sky) {
      this.scene.add(this.sky.mesh);
      this.scene.background = null;
    }
  }

  async initialize(): Promise<void> {
    this.pmrem ??= new PMREMGenerator(this.renderer);
    this.pmrem.compileCubemapShader();
    this.pmrem.compileEquirectangularShader();

    if (this.mergedConfig.hdriPath) {
      try {
        const texture = await this.assets.createHDRLoader().loadAsync(this.mergedConfig.hdriPath);
        texture.mapping = THREE.EquirectangularReflectionMapping;

        this.setEnvironmentMap(this.pmrem.fromEquirectangular(texture));

        // The HDR is also the visible sky — kept alive (not disposed) because
        // scene.background samples it directly.
        this.backgroundTexture = texture;
        this.scene.background = texture;
        if (this.sky) this.scene.remove(this.sky.mesh);
        return;
      } catch (err) {
        console.warn("[env] HDRI sky failed to load — keeping procedural sky", err);
      }
    }

    if (!this.sky) return;

    const renderTarget = this.pmrem.fromScene(this.sky.environmentScene, 0, 0.1, 1500);
    this.setEnvironmentMap(renderTarget);
  }

  private setEnvironmentMap(renderTarget: THREE.WebGLRenderTarget): void {
    if (this.envRenderTarget) this.envRenderTarget.dispose();
    this.envRenderTarget = renderTarget;
    this.scene.environment = renderTarget.texture;
  }

  dispose(): void {
    if (this.sky) this.scene.remove(this.sky.mesh);
    this.daylight.dispose();
    this.scene.environment = null;
    this.scene.background = null;
    this.backgroundTexture?.dispose();
    this.envRenderTarget?.dispose();
    this.pmrem?.dispose();
  }
}
